import React, { useState, useEffect, useCallback } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Link,
  useLocation,
} from "react-router-dom";
import { ArrowLeft, ShieldX } from "lucide-react";
import Formulaire from "./pages/Formulaire";
import Demande from "./pages/Demande";
import Header from "./components/Header";
import Home from "./pages/Home";
import Taches from "./pages/Taches";
import { useIsSignedIn } from "@microsoft/mgt-react"; // Hook pour vérifier si l'utilisateur est connecté via Microsoft Graph Toolkit
import { Providers } from "@microsoft/mgt"; // Fournisseur d'authentification Microsoft
import FormulaireAdresse from "./pages/FormulaireAdresse";
import FormulaireNom from "./pages/FormulaireNom";
import GroupRoles from "./pages/GroupRoles";
import FormulaireReseauInfo from "./pages/FormulaireReseauInfo";
import FormulaireInfoBancaires from "./pages/FormulaireInfoBancaires";
import GestionDonneeFinance from "./pages/GestionDonneeFinance";
import ScrollToTop from "./components/ScrollToTop"; // Composant pour revenir en haut à chaque changement de route
import FormAefo from "./pages/FormAefo";
import { apiFetch } from "./utils/apiClient"; // Wrapper fetch personnalisé
import FormFeeso from "./pages/FormFeeso";
import FormScfp from "./pages/FormScfp";
import ArchivesForms from "./pages/ArchivesForms";
import FormAchatEI from "./pages/FormAchatEI";

import { io } from "socket.io-client";
import AntecedentCriminel from "./pages/AntecedentCriminel";
import AccesHorsPays from "./pages/AccesHorsPays";
import DemandeProjet from "./pages/DemandeProjet";
import EntenteTravailHybrid from "./pages/EntenteTravailHybrid";
import ParticipationFormation from "./pages/ParticipationFormation";
import { showError } from "./components/ErrorPopup";

const socket = io.connect(`${process.env.REACT_APP_API_BASE_URL}`); 

const allowedRedirectPaths = new Set([
  "/demande",
  "/taches",
  "/formulaire",
  "/EntenteTravailHybrid",
  "/achatEquipInfo",
  "/participationFormation",
]);

const MANAGED_FORM_ROUTE_KEYS = Object.freeze({
  "/formulaire": "ReclamationDepense",
  "/formulaireAdresse": "Adresse",
  "/formulaireNom": "Nom",
  "/formulaireInfoBancaires": "InfoBancaire",
  "/formulaireReseauInfo": "ReseauInfo",
  "/antecedentsCriminels": "AntecedentCriminel",
  "/achatEquipInfo": "AchatEI",
  "/EntenteTravailHybrid": "EntenteTravailHybrid",
  "/participationFormation": "ParticipationFormation",
  "/Acceshorspays": "AccesHP",
  "/demandeProjet": "DemandeProjet",
});

function FormAccessDenied() {
  return (
    <main className="flex min-h-[70vh] items-center justify-center px-4 py-12 sm:px-6">
      <section
        className="w-full max-w-3xl"
        role="alert"
        aria-labelledby="access-denied-title"
      >
        <div className="grid md:grid-cols-[220px_1fr]">
          <div className="flex flex-col items-center justify-center border-b border-slate-200 px-6 py-9 text-center md:border-b-0 md:border-r md:py-12">
            <div className="relative flex h-24 w-24 items-center justify-center rounded-full border border-red-200">
              <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-red-600 text-red-700">
                <ShieldX className="h-8 w-8" strokeWidth={1.8} aria-hidden="true" />
              </div>
              <span className="absolute -bottom-2 rounded-full border border-red-200 bg-white px-3 py-0.5 text-xs font-extrabold tracking-wider text-red-700">403</span>
            </div>
            <p className="mt-6 text-xs font-bold uppercase tracking-[0.16em] text-red-700">Accès non autorisé</p>
            <p className="mt-2 text-xs leading-5 text-slate-500">Zone administrative protégée</p>
          </div>

          <div className="px-6 py-8 sm:px-9 sm:py-10">
            <p className="text-sm font-semibold text-red-700">Autorisation requise</p>
            <h1 id="access-denied-title" className="mt-1 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Accès refusé</h1>
            <p className="mt-4 max-w-xl text-sm leading-6 text-slate-600 sm:text-base">
              Votre compte est bien connecté, mais il ne possède pas les droits nécessaires pour consulter cette page.
            </p>
            <div className="mt-7 divide-y divide-slate-200 border-y border-slate-200">
              <div className="grid gap-1 py-4 sm:grid-cols-[120px_1fr] sm:gap-4">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Motif</span>
                <span className="text-sm leading-5 text-slate-700">Cette section est réservée aux utilisateurs disposant des autorisations requises.</span>
              </div>
              <div className="grid gap-1 py-4 sm:grid-cols-[120px_1fr] sm:gap-4">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Que faire?</span>
                <span className="text-sm leading-5 text-slate-700">Si cet accès est requis pour vos fonctions, communiquez avec l’administrateur responsable de l’application.</span>
              </div>
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <Link to="/" className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2">
                <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Retour à l’accueil
              </Link>
              <span className="text-xs text-slate-500">Aucune donnée protégée n’a été affichée.</span>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function AuthenticationRedirect() {
  const location = useLocation();

  if (allowedRedirectPaths.has(location.pathname)) {
    localStorage.setItem(
      "redirectAfterLogin",
      `${location.pathname}${location.search}`,
    );
  }

  return <Navigate to="/" replace />;
}

// import { ToastContainer } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";

function App() {

  // Hook pour vérifier si l'utilisateur est connecté
  const [isSignedIn] = useIsSignedIn();
  // État pour stocker les infos de l'utilisateur
  const [user, setUser] = useState(null);
   // État pour gérer le chargement général et le chargement d'authentification
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);

  // **************************************************************** //
  // États pour vérifier les rôles/groupes auxquels appartient l'utilisateur
  const [checkGroupAdresse, setcheckGroupAdresse] = useState(null);
  const [checkGroupAdmin, setcheckGroupAdmin] = useState(null);
  const [checkGroupNom, setcheckGroupNom] = useState(null);
  const [checkGroupReseauInfo, setCheckGroupReseauInfo] = useState(null);
  const [checkGroupInfoBancaire, setcheckGroupInfoBancaire] = useState(null);
  const [checkGroupAntCriminels, setcheckGroupAntCriminels] = useState(null);
  const [checkGroupRecMensuelle, setcheckGroupRecMensuelle] = useState(null);
  const [checkGroupSeancePlacement, setcheckGroupSeancePlacement] = useState(null);
  const [checkGroupTeletravail, setcheckGroupTeletravail] = useState(null);
  const [checkGroupConsultationAchatEI, setCheckGroupConsultationAchatEI] = useState(null);
  const [checkGroupDemandeProjet, setCheckGroupDemandeProjet] = useState(null);
  const [formAccess, setFormAccess] = useState(null);

  // Informations sur le membre personnel
  const [membrePersonnel, setMembrePersonnel] = useState({});
  const [existMembre, setExistMembre] = useState("");
  // Configuration des routes dynamiques
  const [routesConfig, setRoutesConfig] = useState([]);


  // Fonction pour attendre que le provider Microsoft soit prêt
  const waitForProvider = async () => {
    return new Promise((resolve) => {
      const interval = setInterval(() => {
        const provider = Providers.globalProvider;
        if (provider) {
          clearInterval(interval);
          resolve(provider);
        }
      }, 500); // Vérifie toutes les 500ms
    });
  };
  

  // Fonction pour récupérer les données de l'utilisateur depuis Microsoft Graph
  const fetchUser = useCallback(async () => {
    try {
      const provider = await waitForProvider();
      const graphClient = provider.graph.client;
      // const userData = await graphClient.api("/me").get();
      const userData = await graphClient
        .api(
          "/me?$select=displayName,givenName,jobTitle,employeeId,mail,id,officeLocation"
        )
        .get();

      setUser(userData);
    } catch (error) {
      console.error("Error fetching user data:", error);
    } finally {
      setLoading(false);
    }
  }, []);


  // Fonction pour vérifier les rôles/groupes de l'utilisateur via l'API backend
  const checkuser = useCallback(async () => {
    // Check if user exists in AdminRolesAdresse
    if (user) {
      
          const response = await apiFetch(
            `${process.env.REACT_APP_API_BASE_URL}/checkUserRole`
          );
          const data = await response.json();
          if (!response.ok) {
            throw new Error(data.error || data.message);
          }
          
          // Mise à jour des états de rôles/groupes
          setcheckGroupAdresse(data.GroupRolesAdresse.exists);
          setcheckGroupAdmin(data.GroupRolesAdmin.exists);
          setcheckGroupNom(data.GroupRolesNom.exists);
          setCheckGroupReseauInfo(data.GroupRolesReseauInf.exists);
          setcheckGroupInfoBancaire(data.GroupRolesInfoBancaire.exists);
          setcheckGroupAntCriminels(data.GroupRolesAntCriminels.exists);
          setcheckGroupRecMensuelle(data.GroupRolesRecMensuelle.exists);
          setcheckGroupSeancePlacement(data.GroupRolesSeancePlacement.exists)
          setcheckGroupTeletravail(data.GroupRolesTeletravail.exists)
          setCheckGroupConsultationAchatEI(data.GroupRolesConsultationAchatEI.exists)
          setCheckGroupDemandeProjet(
            data.GroupRolesDemandeProjet?.exists ?? false
          );
          setFormAccess(
            data.formAccess ?? {
              restricted: true,
              allowedForms: [],
            },
          );
        
    }
  }, [user]);


  // Hook useEffect pour lancer la récupération utilisateur dès la connexion
  useEffect(() => {
    
    if (isSignedIn) {
      fetchUser();
    } else {
      // Si non connecté, on reset tout
      setUser(null);
      setLoading(false);
       setcheckGroupAdresse(null);
       setcheckGroupAdmin(null);
       setCheckGroupDemandeProjet(null);
       setFormAccess(null);
    }
    // Stop le chargement d'authentification après 1s
    setTimeout(() => {
      setAuthLoading(false); 
    }, 1000);
  }, [isSignedIn, fetchUser]);

  // Vérifie les rôles de l'utilisateur dès que ses infos sont disponibles
  useEffect(() => {
    if (user) {
      checkuser();
    }
  }, [user, checkuser]);

  // Récupération dynamique de la configuration des routes depuis l'API
useEffect(() => {
    async function fetchRoutes() {
      try {
        const res = await apiFetch(
          `${process.env.REACT_APP_API_BASE_URL}/routeConfigSchema`
        );
        const data = await res.json();

         // Map chaque route à son composant React correspondant
        const mappedRoutes = data.map((route) => ({
          ...route,
          element: () =>
            getRouteElement(
              // route.path,
              route, // 👈 ON PASSE LA ROUTE
              data,  // 👈 TOUTES LES ROUTES (routesConfig)
              user,
              checkGroupRecMensuelle,
              checkGroupAdmin,
              checkGroupSeancePlacement,
              checkGroupConsultationAchatEI,
              checkGroupDemandeProjet
            ),
        }));
        setRoutesConfig(mappedRoutes);
      } catch (err) {
        console.error("Erreur récupération routes:", err);
      }
    }
    fetchRoutes();
    // 🔔 écouter les mises à jour
  socket.on("routesUpdated", () => {
    fetchRoutes();
  });

  return () => {
    socket.off("routesUpdated");
  };
  }, [user, checkGroupRecMensuelle, checkGroupAdmin, checkGroupSeancePlacement, checkGroupConsultationAchatEI, checkGroupDemandeProjet, membrePersonnel]);
  // useEffect(() => {
  //   async function fetchRoutes() {
  //     try {
  //       const res = await apiFetch(
  //         `${process.env.REACT_APP_API_BASE_URL}/routeConfigSchema`
  //       );
  //       const data = await res.json();

  //        // Map chaque route à son composant React correspondant
  //       const mappedRoutes = data.map((route) => ({
  //         ...route,
  //         element: () =>
  //           getRouteElement(
  //             route.path,
  //             user,
  //             checkGroupRecMensuelle,
  //             checkGroupAdmin,
  //             checkGroupSeancePlacement
  //           ),
  //       }));
  //       setRoutesConfig(mappedRoutes);
  //     } catch (err) {
  //       console.error("Erreur récupération routes:", err);
  //     }
  //   }
  //   fetchRoutes();
  // }, [user, checkGroupRecMensuelle, checkGroupAdmin, checkGroupSeancePlacement, membrePersonnel]);


  // Récupération des infos du membre personnel (Séance de placemenet) depuis l'API
  const fetchMembrePersonnel = useCallback(async () => {
    try {
      const res = await apiFetch(
        `${process.env.REACT_APP_API_BASE_URL}/listePersonnel?matricule=${encodeURIComponent(user.employeeId)}`
      );

      const data = await res.json();
      if (!res.ok) {
            throw new Error(data.error || data.message);
          }

      if (data) {
        setMembrePersonnel(data.personnel || {});
        setExistMembre(data.exist || "");
      }

    } catch (error) {
        console.error(error);
        showError(error); // ou setError(error.message)
      }
  }, [user?.mail]);


  // Appelle fetchMembrePersonnel dès que l'email de l'utilisateur est disponible
  useEffect(() => {
    if (user?.mail) {
      fetchMembrePersonnel();
    } else {
      setMembrePersonnel({});
      setExistMembre("");
    }
  }, [user, fetchMembrePersonnel]);

  // Fonction pour associer un chemin de route à son composant React
  function getRouteElement(
    // path,
    route,
    routesConfig,
    user,
    checkGroupRecMensuelle,
    checkGroupAdmin,
    checkGroupSeancePlacement,
    checkGroupConsultationAchatEI,
    checkGroupDemandeProjet
  ) {

    const { path, enabled } = route;

    switch (path) {
      case "/formulaire":
        return (
          <Formulaire
            user={user}
            checkGroupRecMensuelle={checkGroupRecMensuelle}
            checkGroupAdmin={checkGroupAdmin}
            enabled={enabled}
            // enabled={routesConfig.find(r => r.label === "Réclamation mensuelle de dépenses")?.enabled}
          />
        );
      case "/formulaireAdresse":
        return <FormulaireAdresse user={user} />;
      case "/formulaireNom":
        return <FormulaireNom user={user} />;
      case "/formulaireInfoBancaires":
        return <FormulaireInfoBancaires user={user} />;
      case "/formulaireReseauInfo":
        return <FormulaireReseauInfo user={user} />;
      case "/achatEquipInfo":
        return <FormAchatEI user={user} enabled={enabled} checkGroupAdmin={checkGroupAdmin} checkGroupConsultationAchatEI={checkGroupConsultationAchatEI} />
      case "/EntenteTravailHybrid":
        return  <EntenteTravailHybrid user={user} enabled={enabled}/>
      case "/antecedentsCriminels":
        return <AntecedentCriminel user={user} enabled={enabled}/>;
      case "/Acceshorspays":
        return <AccesHorsPays user={user} enabled={enabled} checkGroupAdmin={checkGroupAdmin}/>;
      case "/demandeProjet":
        return <DemandeProjet user={user} enabled={enabled} checkGroupDemandeProjet={checkGroupDemandeProjet}/>;
      case "/participationFormation":
        return <ParticipationFormation user={user} enabled={enabled} checkGroupAdmin={checkGroupAdmin}/>;
      case "/formAefo":
        // ✅ Render FormAefo only if existMembre === "exist dans AEFO"
        return (existMembre === "exist dans AEFO" || checkGroupAdmin || checkGroupSeancePlacement)

          ? <FormAefo
            user={user}
            membrePersonnel={membrePersonnel}
            checkGroupAdmin={checkGroupAdmin}
            checkGroupSeancePlacement={checkGroupSeancePlacement}
            routesConfig={routesConfig.filter(r => r.label === "Form AEFO")}
          enabled={enabled}
            // routesConfig={routesConfig.filter(route => route.label === "Form AEFO")}
            // enabled={routesConfig.find(r => r.label === "Form AEFO")?.enabled}
          />
          : null;
      case "/formFeeso":
        return (existMembre === "exist dans FEESO" || checkGroupAdmin || checkGroupSeancePlacement)

          ? <FormFeeso
            user={user}
            membrePersonnel={membrePersonnel}
            checkGroupAdmin={checkGroupAdmin}
            checkGroupSeancePlacement={checkGroupSeancePlacement}
            routesConfig={routesConfig.filter(r => r.label === "Form FEESO")}
          enabled={enabled}
            // routesConfig={routesConfig.filter(route => route.label === "Form FEESO")}
            // enabled={routesConfig.find(r => r.label === "Form FEESO")?.enabled}
          />
          : null;
      case "/formScfp":
        return (existMembre === "exist dans SCFP" || checkGroupAdmin || checkGroupSeancePlacement)

          ? <FormScfp
            user={user}
            membrePersonnel={membrePersonnel}
            checkGroupAdmin={checkGroupAdmin}
            checkGroupSeancePlacement={checkGroupSeancePlacement}
            routesConfig={routesConfig.filter(r => r.label === "Form SCFP")}
          enabled={enabled}
            // routesConfig={routesConfig.filter(route => route.label === "Form SCFP")}
            // enabled={routesConfig.find(r => r.label === "Form SCFP")?.enabled}
          />
          : null;

      default:
        return null;
    }
  }

  // **************************************************************** //
  // Affichage d'un loader pendant l'authentification
  if (authLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-75 z-50">
        <div className="w-12 h-12 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
        <p className="ml-3 text-gray-700 font-medium">Chargement en cours...</p>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <ScrollToTop />
       {/* Toastify GLOBAL */}
    {/* <ToastContainer
      position="top-center"
      autoClose={3000}
      hideProgressBar={false}
      closeOnClick
      pauseOnHover
      draggable
    /> */}
      <Routes>
        <Route
          path="/"
          element={
            <Header
              isSignedIn={isSignedIn}
              user={user}
              checkGroupAdresse={checkGroupAdresse}
              checkGroupAdmin={checkGroupAdmin}
              checkGroupNom={checkGroupNom}
              checkGroupReseauInfo={checkGroupReseauInfo}
              checkGroupInfoBancaire={checkGroupInfoBancaire}
              checkGroupAntCriminels={checkGroupAntCriminels}
              checkGroupRecMensuelle={checkGroupRecMensuelle}
              checkGroupSeancePlacement={checkGroupSeancePlacement}
              checkGroupTeletravail={checkGroupTeletravail}
              checkGroupConsultationAchatEI={checkGroupConsultationAchatEI}
              checkGroupDemandeProjet={checkGroupDemandeProjet}
              formAccess={formAccess}
              routesConfig={routesConfig}
              existMembre={existMembre}
            />
          }
        >
          <Route
            index
            element={
              <Home
                isSignedIn={isSignedIn}
                user={user}
              />
            }
          />

          {isSignedIn ? (
            <>
              {routesConfig.map((r) => {
                const managedFormKey = MANAGED_FORM_ROUTE_KEYS[r.path];
                if (managedFormKey) {
                  if (formAccess === null) {
                    return (
                      <Route
                        key={r.path}
                        path={r.path}
                        element={
                          <div className="flex min-h-[60vh] items-center justify-center text-sm text-slate-600">
                            Vérification de vos accès...
                          </div>
                        }
                      />
                    );
                  }

                  const canAccessForm =
                    formAccess.restricted === false ||
                    formAccess.allowedForms?.includes(managedFormKey);

                  if (!canAccessForm) {
                    return (
                      <Route
                        key={r.path}
                        path={r.path}
                        element={<FormAccessDenied />}
                      />
                    );
                  }
                }

                // For FormAefo, we always render the route
                if (r.label === "Form SCFP" || r.label === "Form FEESO" 
                  || r.label === "Form AEFO" || r.label === "Réclamation mensuelle de dépenses" 
                  || r.label === "Achat équipement informatique" || r.label === "Antécédents criminels"
                  || r.label === "Accès hors pays" || r.label === "Demande de projet" || r.label==="Entente de travail en mode hybride"
                 || r.label==="Participation à une formation")
                {
                  return <Route key={r.path} path={r.path} element={r.element()} />;
                }

                // For other routes, filter by enabled
                if (!r.enabled) return null;

                return <Route key={r.path} path={r.path} element={r.element()} />;
              })}

              <Route
                path="/demande"
                element={
                  <Demande
                    user={user}
                    checkGroupDemandeProjet={checkGroupDemandeProjet}
                    checkGroupAdmin={checkGroupAdmin}
                    checkGroupSeancePlacement={checkGroupSeancePlacement}
                    existMembre={existMembre}
                    formAccess={formAccess}
                  />
                }
              />
              

              <Route
                path="/gestionDonnéesFinancières"
                element={
                  checkGroupAdmin ||
                  checkGroupRecMensuelle ||
                  checkGroupSeancePlacement ||
                  checkGroupAntCriminels ||
                  checkGroupTeletravail ? (
                    <GestionDonneeFinance
                      user={user}
                      checkGroupRecMensuelle={checkGroupRecMensuelle}
                      checkGroupAdmin={checkGroupAdmin}
                      checkGroupNom={checkGroupNom}
                      checkGroupAdresse={checkGroupAdresse}
                      checkGroupInfoBancaire={checkGroupInfoBancaire}
                      checkGroupAntCriminels={checkGroupAntCriminels}
                      checkGroupSeancePlacement={checkGroupSeancePlacement}
                      checkGroupTeletravail={checkGroupTeletravail}
                      routesConfig={routesConfig.filter(route => ["Form AEFO", "Form FEESO", "Form SCFP"].includes(route.label))}
                      setRoutesConfig={setRoutesConfig}
                    />
                  ) : (
                    <FormAccessDenied />
                  )
                }
              />
              {(checkGroupAdmin || checkGroupRecMensuelle ||checkGroupInfoBancaire || checkGroupAntCriminels) && (
                <Route
                  path="/Archives"
                  element={
                    <ArchivesForms
                      user={user}
                      checkGroupRecMensuelle={checkGroupRecMensuelle}
                      checkGroupAdmin={checkGroupAdmin}
                      checkGroupInfoBancaire={checkGroupInfoBancaire}
                      checkGroupAntCriminels={checkGroupAntCriminels}
                    />
                  }
                />
              )}

              <Route
                path="/taches"
                element={
                  <Taches
                    user={user}
                    checkGroupNom={checkGroupNom}
                    checkGroupAdresse={checkGroupAdresse}
                    checkGroupAdmin={checkGroupAdmin}
                    checkGroupReseauInfo={checkGroupReseauInfo}
                    checkGroupInfoBancaire={checkGroupInfoBancaire}
                    checkGroupAntCriminels={checkGroupAntCriminels}
                    checkGroupRecMensuelle={checkGroupRecMensuelle}
                    checkGroupSeancePlacement={checkGroupSeancePlacement}
                    checkGroupTeletravail={checkGroupTeletravail}
                    checkGroupConsultationAchatEI={checkGroupConsultationAchatEI}
                    checkGroupDemandeProjet={checkGroupDemandeProjet}
                    formAccess={formAccess}
                    loading={loading}
                  />
                }
              />
              {/* )} */}

              <Route
                path="/groupRoles"
                element={
                  checkGroupAdmin ? (
                    <GroupRoles
                      user={user}
                      routesConfig={routesConfig}
                      setRoutesConfig={setRoutesConfig}
                    />
                  ) : (
                    <FormAccessDenied />
                  )
                }
              />
            </>
          ) : (
            <Route
              path="*"
              element={
                authLoading ? (
                  <div>Chargement en cours...</div>
                ) : (
                  <AuthenticationRedirect />
                )
              }
            />
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;





